import React from 'react';
import { clsx } from 'clsx';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size    = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
}

const variantClass: Record<Variant, string> = {
  // Round 145: one focus ring for every variant - the theme's focus token, 3:1+ against the page in both themes (amber/30 was faint on cream)
  primary:   'bg-amber text-on-amber hover:bg-amber-hover focus:ring-focus disabled:bg-amber/50',
  secondary: 'bg-card text-ink border border-line-strong hover:bg-paper-2 focus:ring-focus',
  ghost:     'bg-transparent text-amber-strong hover:bg-amber/10 focus:ring-focus',
  danger:    'bg-red-600 text-white hover:bg-red-700 focus:ring-focus',
};

const sizeClass: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-sm min-h-[36px]',
  md: 'px-5 py-2.5 text-sm min-h-[44px]',
  lg: 'px-6 py-3 text-base min-h-[52px]',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  className,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-md font-ui tracking-[0.01em]',
        'transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-paper',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variantClass[variant],
        sizeClass[size],
        fullWidth && 'w-full',
        className,
      )}
    >
      {loading && (
        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      )}
      {children}
    </button>
  );
}
